import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { confirmation } from '@/hooks/use-confirmation';
import {
  Folder,
  getFolders,
  loadFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  subscribeFolders,
  canDeleteFolder,
  FolderCreateRequest,
  FolderUpdateRequest
} from '@/lib/stores/folderStore';

interface UseFolderManagementProps {
  itemType: 'library' | 'documents' | 'blueprints' | 'dialogs' | 'flows' | 'variables';
  enabled?: boolean;
}

export function useFolderManagement({ itemType, enabled = true }: UseFolderManagementProps) {
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  
  // State for newly created folder (to trigger edit mode)
  const [newlyCreatedFolderId, setNewlyCreatedFolderId] = useState<string | null>(null);

  // Reset to root (null) when game actually changes (not on every re-render)
  const prevGameIdRef = useRef<string | undefined>(undefined);
  const prevItemTypeRef = useRef<string | undefined>(undefined);
  const hasLoadedOnceRef = useRef(false);
  
  useEffect(() => {
    const currentGameId = selectedGame?.id;
    const currentItemType = itemType;
    
    // Only reset if game ID actually changed (not just undefined -> undefined)
    const gameChanged = prevGameIdRef.current !== undefined && 
                       prevGameIdRef.current !== currentGameId;
    
    // Only reset if item type actually changed (not just undefined -> undefined)  
    const itemTypeChanged = prevItemTypeRef.current !== undefined && 
                           prevItemTypeRef.current !== currentItemType;
    
    if (gameChanged || itemTypeChanged) {
     
      setSelectedFolderId(null);
      
      // Don't show loading state when switching types if we've loaded once
      // This prevents flickering during transitions
      if (!hasLoadedOnceRef.current) {
        setIsLoading(true);
      }
    }
    
    // Update refs for next comparison
    prevGameIdRef.current = currentGameId;
    prevItemTypeRef.current = currentItemType;
  }, [selectedGame?.id, itemType]);

  // Load folders when game or item type changes
  useEffect(() => {
    if (!enabled) {
      setFolders([]);
      setIsLoading(false);
      return;
    }

    if (!selectedGame?.id) {
      setFolders([]);
      setIsLoading(false);
      return;
    }

    const loadFolderData = async () => {
      // Only show loading state on first load or game change
      // For item type changes, load silently to prevent flickering
      const shouldShowLoading = !hasLoadedOnceRef.current || 
                               prevGameIdRef.current !== selectedGame.id;
      
      if (shouldShowLoading) {
        setIsLoading(true);
      }
      
      try {
        await loadFolders(selectedGame.id, itemType, selectedGame);
        const gameFolders = getFolders(selectedGame.id, itemType);
        setFolders(gameFolders);
        hasLoadedOnceRef.current = true;
      } catch (error) {
        console.error('Failed to load folders:', error);
        toast({
          title: 'Failed to load folders',
          description: 'Failed to load folders',
          variant: 'destructive'
        });
      } finally {
        if (shouldShowLoading) {
          setIsLoading(false);
        }
      }
    };

    loadFolderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame?.id, itemType, enabled]); // Intentionally omitting selectedGame and toast to avoid infinite loops

  // Subscribe to folder store changes
  useEffect(() => {
    if (!enabled || !selectedGame?.id) return;

    const unsubscribe = subscribeFolders(() => {
      const gameFolders = getFolders(selectedGame.id, itemType);
      setFolders(gameFolders);
    });

    return unsubscribe;
  }, [selectedGame?.id, itemType, enabled]);

  const handleFolderSelect = (folderId: string | null) => {
    setSelectedFolderId(folderId);
  };

  const handleFolderCreate = async (parentId?: string, name?: string) => {
    if (!selectedGame?.id) return;

    // Always create folder immediately with default name if not provided
    const folderName = name?.trim() || 'New Folder';
    
    try {
      // Map 'blueprints' to 'templates' for folder store compatibility
      const folderItemType = itemType === 'blueprints' ? 'templates' : itemType;
      const folderData: FolderCreateRequest = {
        name: folderName,
        parentId,
        gameId: selectedGame.id,
        itemType: folderItemType as 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables'
      };

      const newFolder = await createFolder(folderData, toast);
      
      // Set the newly created folder ID to trigger edit mode
      setNewlyCreatedFolderId(newFolder.id);
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const handleFolderRename = async (folderId: string, newName: string) => {
    if (!selectedGame?.id || !newName.trim()) return;
    // Map 'blueprints' to 'templates' for folder store compatibility
    const folderItemType = itemType === 'blueprints' ? 'templates' : itemType;
    try {
      const updateData: FolderUpdateRequest = {
        id: folderId,
        name: newName.trim(),
        gameId: selectedGame.id,
        itemType: folderItemType as 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables'
      };

      await updateFolder(updateData, toast);
    } catch (error) {
      console.error('Failed to rename folder:', error);
    }
  };

  const handleFolderColorChange = async (folderId: string, color: string | null) => {
    if (!selectedGame?.id) return;
    // Map 'blueprints' to 'templates' for folder store compatibility
    const folderItemType = itemType === 'blueprints' ? 'templates' : itemType;
    try {
      const updateData: FolderUpdateRequest = {
        id: folderId,
        color: color || undefined,
        gameId: selectedGame.id,
        itemType: folderItemType as 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables'
      };

      await updateFolder(updateData, toast);
    } catch (error) {
      console.error('Failed to update folder color:', error);
    }
  };

  const handleFolderDelete = async (folderId: string) => {
    if (!selectedGame?.id) return;

    // Check if folder can be deleted
    const validation = canDeleteFolder(folderId, selectedGame.id, itemType);
    if (!validation.canDelete) {
      toast({
        title: 'Cannot delete folder',
        description: validation.reason,
        variant: 'destructive'
      });
      return;
    }

    // Get folder name for the confirmation message
    const findFolderById = (folders: Folder[], id: string): Folder | null => {
      for (const folder of folders) {
        if (folder.id === id) return folder;
        if (folder.subfolder) {
          const found = findFolderById(folder.subfolder, id);
          if (found) return found;
        }
      }
      return null;
    };

    const folderToDelete = findFolderById(folders, folderId);
    const folderName = folderToDelete?.name || 'this folder';

    const confirmDialog = confirmation({
      title: 'Delete Folder',
      description: `Are you sure you want to delete "${folderName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await deleteFolder(folderId, selectedGame.id, itemType, toast);
          
          // If we're currently viewing the deleted folder, reset to root (null)
          if (selectedFolderId === folderId) {
            setSelectedFolderId(null);
          }
        } catch (error) {
          console.error('Failed to delete folder:', error);
        }
        confirmDialog.dismiss();
      },
      onCancel: () => {
        confirmDialog.dismiss();
      }
    });
  };

  const handleFolderMove = async (folderId: string, newParentId: string | null) => {
    if (!selectedGame?.id) return;

    try {
      // Recursively find folder in the tree (including nested subfolders)
      const findFolder = (folderList: Folder[]): Folder | null => {
        for (const folder of folderList) {
          if (folder.id === folderId) {
            return folder;
          }
          // Check both children and subfolder properties for compatibility
          const subfolders = (folder as any).children || folder.subfolder || [];
          if (subfolders.length > 0) {
            const found = findFolder(subfolders);
            if (found) return found;
          }
        }
        return null;
      };

      const currentFolder = findFolder(folders);
      if (!currentFolder) {
        console.error('Folder not found:', folderId, 'in folders:', folders);
        toast({
          title: 'Folder not found',
          description: 'The folder could not be located',
          variant: 'destructive'
        });
        return;
      }


      // Prevent moving a folder into itself or its descendants
      if (newParentId === folderId) {
        toast({
          title: 'Invalid move',
          description: 'Cannot move a folder into itself',
          variant: 'destructive'
        });
        return;
      }

      // Map 'blueprints' to 'templates' for folder store compatibility
      const folderItemType = itemType === 'blueprints' ? 'templates' : itemType;
      
      // Update the folder with new parent
      const updateData: FolderUpdateRequest = {
        id: folderId,
        name: currentFolder.name,
        gameId: selectedGame.id,
        itemType: folderItemType as 'documents' | 'templates' | 'library' | 'dialogs' | 'flows' | 'variables',
        parentId: newParentId
      };

      await updateFolder(updateData, toast);

      toast({
        title: 'Folder moved',
        description: `"${currentFolder.name}" has been moved successfully`,
      });
    } catch (error) {
      console.error('Failed to move folder:', error);
      toast({
        title: 'Failed to move folder',
        description: error instanceof Error ? error.message : 'An error occurred while moving the folder',
        variant: 'destructive'
      });
    }
  };

  // Helper function to get items filtered by selected folder
  const filterItemsByFolder = <T extends { folderId?: string }>(items: T[]): T[] => {
    // Always show all items (no folder filtering)
    return items;
  };

  const handleItemDrop = async (itemId: string, folderId: string | null) => {
    if (!selectedGame?.id) return;

    try {
      // Emit a custom event that the page can listen to
      // Don't show toast here - let the actual update handler show it
      console.log("handleItemDrop", itemId, folderId);
      window.dispatchEvent(new CustomEvent('moveItemToFolder', {
        detail: { itemId, folderId, gameId: selectedGame.id, itemType }
      }));
    } catch (error) {
      console.error('Failed to move item:', error);
      toast({
        title: 'Failed to move item',
        description: 'Failed to move item',
        variant: 'destructive'
      });
    }
  };

  // Helper function to check if a folder can be deleted
  const checkCanDeleteFolder = (folderId: string): { canDelete: boolean; reason?: string } => {
    if (!selectedGame?.id) return { canDelete: false, reason: 'No game selected' };
    return canDeleteFolder(folderId, selectedGame.id, itemType);
  };

  const clearNewlyCreatedFolder = () => {
    setNewlyCreatedFolderId(null);
  };

  return {
    folders,
    selectedFolderId,
    isLoading,
    handleFolderSelect,
    handleFolderCreate,
    handleFolderRename,
    handleFolderDelete,
    handleFolderColorChange,
    handleFolderMove,
    handleItemDrop,
    filterItemsByFolder,
    checkCanDeleteFolder,
    gameId: selectedGame?.id,
    // Newly created folder tracking
    newlyCreatedFolderId,
    clearNewlyCreatedFolder
  };
}
